import React, { useContext } from "react"
import { useTranslation } from "react-i18next"

import { DragSourceDataContext } from "shared/context/drag-and-drop/DragSourceDataContext"
import { useDroppable } from "shared/lib/drag-and-drop/useDroppable"
import { useEvent } from "shared/lib/useEvent"
import { DropHandler } from "shared/types/DragAndDrop"
import { Floor } from "shared/types/Floor"
import {
  FloorTypename,
  ProductPositionTypename,
  RoomTypename
} from "shared/types/Project"
import { isDragSource } from "shared/types/drag-and-drop/DragSource"

import z from "zod"

export interface FloorDropzoneProps {
  floor: Floor
  onDrop: DropHandler
}

export const FloorDropzone = React.memo(
  ({ floor, onDrop }: FloorDropzoneProps) => {
    const { t } = useTranslation()
    const { dragSource } = useContext(DragSourceDataContext)
    const dropTargetID = `drop-floor-${floor.id}`

    const handleDrop = useEvent(() => {
      if (
        isDragSource(dragSource, {
          dataSchema: z.object({
            id: z.string(),
            __typename: z.literal(RoomTypename)
          })
        })
      ) {
        onDrop(
          dragSource.data.id,
          dragSource.meta.index,
          dragSource.meta.parentID,
          0,
          floor.id
        )
      }
    })

    const invalidDrop = dragSource?.type === ProductPositionTypename

    const { isOver, setNodeRef } = useDroppable<Floor, unknown>({
      id: dropTargetID,
      data: floor,
      type: FloorTypename,
      accept: [RoomTypename, ProductPositionTypename],
      error: invalidDrop,
      onDragEnd: handleDrop
    })

    return (
      <div
        ref={setNodeRef}
        className="floor-dropzone"
        data-testid="floor-dropzone"
        data-drop-zone=""
        data-drop-zone-active={isOver ? "" : undefined}
        data-drop-zone-error={invalidDrop ? "" : undefined}
      >
        {invalidDrop ? (
          <div className="floor-dropzone__text">
            {t("Project.FloorDropzoneError")}
          </div>
        ) : (
          <div className="floor-dropzone__text">
            {t("Project.FloorDropzoneText")}
          </div>
        )}
      </div>
    )
  }
)
